<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  ArrowLeftToLine,
  ArrowRightToLine,
  ChevronDown,
  MinusCircle,
  RotateCw,
  Trash2,
  X
} from '@lucide/vue'
import type { AdminLayoutWorkspaceTab } from '../../types'

const props = withDefaults(
  defineProps<{
    workspaceTabs: AdminLayoutWorkspaceTab[]
    activeWorkspaceTabId: string
  }>(),
  {
    workspaceTabs: () => [],
    activeWorkspaceTabId: ''
  }
)

const emit = defineEmits<{
  'tab-click': [tab: AdminLayoutWorkspaceTab]
  'tab-close': [tab: AdminLayoutWorkspaceTab]
  'tab-refresh': [tab: AdminLayoutWorkspaceTab]
  'close-other': [tab?: AdminLayoutWorkspaceTab]
  'close-all': []
  'close-left': [tab: AdminLayoutWorkspaceTab]
  'close-right': [tab: AdminLayoutWorkspaceTab]
}>()

const tabsNavRef = ref<HTMLElement | null>(null)
const actionMenuOpen = ref(false)
const actionMenuWrapRef = ref<HTMLElement | null>(null)

// Context Menu State
const contextMenuOpen = ref(false)
const contextMenuX = ref(0)
const contextMenuY = ref(0)
const contextTargetTab = ref<AdminLayoutWorkspaceTab | null>(null)

const activeTab = computed(() =>
  props.workspaceTabs.find((t) => t.id === props.activeWorkspaceTabId) ?? null
)

const activeTabIndex = computed(() =>
  props.workspaceTabs.findIndex((t) => t.id === props.activeWorkspaceTabId)
)

const hasClosableTabs = computed(() =>
  props.workspaceTabs.some((t) => t.closable)
)

function getTabIndex(tab: AdminLayoutWorkspaceTab | null): number {
  if (!tab) return -1
  return props.workspaceTabs.findIndex((t) => t.id === tab.id)
}

function canCloseLeftFor(tab: AdminLayoutWorkspaceTab | null): boolean {
  const index = getTabIndex(tab)
  if (index <= 0) return false
  return props.workspaceTabs.slice(0, index).some((t) => t.closable)
}

function canCloseRightFor(tab: AdminLayoutWorkspaceTab | null): boolean {
  const index = getTabIndex(tab)
  if (index < 0 || index >= props.workspaceTabs.length - 1) return false
  return props.workspaceTabs.slice(index + 1).some((t) => t.closable)
}

function canCloseOtherFor(tab: AdminLayoutWorkspaceTab | null): boolean {
  if (!tab) return false
  return props.workspaceTabs.some((t) => t.id !== tab.id && t.closable)
}

function handleWheel(event: WheelEvent) {
  if (tabsNavRef.value) {
    tabsNavRef.value.scrollLeft += event.deltaY
  }
}

function handleTabClick(tab: AdminLayoutWorkspaceTab) {
  closeAllMenus()
  emit('tab-click', tab)
}

function handleTabClose(tab: AdminLayoutWorkspaceTab) {
  closeAllMenus()
  if (tab.closable) {
    emit('tab-close', tab)
  }
}

function handleTabDblClick(tab: AdminLayoutWorkspaceTab) {
  if (tab.closable) {
    handleTabClose(tab)
  }
}

function handleTabMouseUp(event: MouseEvent, tab: AdminLayoutWorkspaceTab) {
  // Middle click to close tab
  if (event.button === 1 && tab.closable) {
    event.preventDefault()
    handleTabClose(tab)
  }
}

// Context Menu Handlers
function handleContextMenu(event: MouseEvent, tab: AdminLayoutWorkspaceTab) {
  event.preventDefault()
  event.stopPropagation()
  actionMenuOpen.value = false
  contextTargetTab.value = tab

  const menuWidth = 148
  const menuHeight = 220
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1000
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800

  contextMenuX.value = Math.max(8, Math.min(event.clientX, viewportWidth - menuWidth - 8))
  contextMenuY.value = Math.max(8, Math.min(event.clientY, viewportHeight - menuHeight - 8))
  contextMenuOpen.value = true
}

function closeAllMenus() {
  actionMenuOpen.value = false
  contextMenuOpen.value = false
  contextTargetTab.value = null
}

function handleRefreshTarget(tab: AdminLayoutWorkspaceTab | null) {
  const target = tab ?? activeTab.value
  closeAllMenus()
  if (target) {
    emit('tab-refresh', target)
  }
}

function handleCloseTarget(tab: AdminLayoutWorkspaceTab | null) {
  closeAllMenus()
  if (tab?.closable) {
    emit('tab-close', tab)
  }
}

function handleCloseLeftTarget(tab: AdminLayoutWorkspaceTab | null) {
  closeAllMenus()
  if (tab && canCloseLeftFor(tab)) {
    emit('close-left', tab)
  }
}

function handleCloseRightTarget(tab: AdminLayoutWorkspaceTab | null) {
  closeAllMenus()
  if (tab && canCloseRightFor(tab)) {
    emit('close-right', tab)
  }
}

function handleCloseOtherTarget(tab: AdminLayoutWorkspaceTab | null) {
  closeAllMenus()
  if (tab && canCloseOtherFor(tab)) {
    emit('close-other', tab)
  }
}

function handleCloseAllTarget() {
  closeAllMenus()
  if (hasClosableTabs.value) {
    emit('close-all')
  }
}

function toggleActionMenu() {
  contextMenuOpen.value = false
  actionMenuOpen.value = !actionMenuOpen.value
}

function handleGlobalPointerDown(event: PointerEvent) {
  const target = event.target as HTMLElement | null
  if (target?.closest('.admin-shell__context-menu') || target?.closest('.admin-shell__tabs-actions')) {
    return
  }
  closeAllMenus()
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    closeAllMenus()
  }
}

onMounted(() => {
  if (typeof document !== 'undefined') {
    document.addEventListener('pointerdown', handleGlobalPointerDown)
    document.addEventListener('keydown', handleKeydown)
  }
})

onBeforeUnmount(() => {
  if (typeof document !== 'undefined') {
    document.removeEventListener('pointerdown', handleGlobalPointerDown)
    document.removeEventListener('keydown', handleKeydown)
  }
})

watch(
  () => props.activeWorkspaceTabId,
  async () => {
    await nextTick()
    if (tabsNavRef.value) {
      const activeEl = tabsNavRef.value.querySelector('.admin-shell__tab.active') as HTMLElement | null
      if (activeEl) {
        activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
      }
    }
  }
)
</script>

<template>
  <nav class="admin-shell__tabs-container" aria-label="已打开标签页">
    <div
      ref="tabsNavRef"
      class="admin-shell__tabs-scroll"
      @wheel.passive="handleWheel"
    >
      <div
        v-for="tab in workspaceTabs"
        :key="tab.id"
        class="admin-shell__tab"
        :class="{
          active: activeWorkspaceTabId === tab.id,
          'is-affix': !tab.closable
        }"
        :title="tab.label"
        role="button"
        tabindex="0"
        @click="handleTabClick(tab)"
        @dblclick.prevent="handleTabDblClick(tab)"
        @mouseup="handleTabMouseUp($event, tab)"
        @contextmenu="handleContextMenu($event, tab)"
        @keydown.enter="handleTabClick(tab)"
      >
        <span v-if="activeWorkspaceTabId === tab.id" class="admin-shell__tab-dot" aria-hidden="true" />
        <span class="admin-shell__tab-label">{{ tab.label }}</span>
        <button
          v-if="tab.closable"
          type="button"
          class="admin-shell__tab-close-btn"
          title="关闭 (或双击标签)"
          aria-label="关闭此标签"
          @click.stop="handleTabClose(tab)"
          @keydown.enter.stop.prevent="handleTabClose(tab)"
        >
          <X :size="12" :stroke-width="2" aria-hidden="true" />
        </button>
      </div>
    </div>

    <!-- Right-side Utility Tools -->
    <div ref="actionMenuWrapRef" class="admin-shell__tabs-actions">
      <!-- Quick Refresh Active Tab Button -->
      <button
        type="button"
        class="admin-shell__tabs-tool-btn"
        title="刷新当前标签页"
        aria-label="刷新当前标签页"
        @click="handleRefreshTarget(activeTab)"
      >
        <RotateCw :size="13" :stroke-width="1.8" aria-hidden="true" />
      </button>

      <!-- Dropdown Menu Trigger -->
      <button
        type="button"
        class="admin-shell__tabs-tool-btn"
        :class="{ 'is-open': actionMenuOpen }"
        title="标签页操作"
        aria-label="标签页操作菜单"
        @click.stop="toggleActionMenu"
      >
        <ChevronDown :size="14" :stroke-width="2" aria-hidden="true" />
      </button>

      <!-- Dropdown Menu List -->
      <div v-if="actionMenuOpen" class="admin-shell__tabs-menu">
        <button
          type="button"
          class="admin-shell__tabs-menu-item"
          @click="handleRefreshTarget(activeTab)"
        >
          <RotateCw :size="13" class="admin-shell__menu-icon" aria-hidden="true" />
          <span>重新加载</span>
        </button>
        <button
          type="button"
          class="admin-shell__tabs-menu-item"
          :disabled="!activeTab?.closable"
          @click="handleCloseTarget(activeTab)"
        >
          <X :size="13" class="admin-shell__menu-icon" aria-hidden="true" />
          <span>关闭当前</span>
        </button>
        <button
          type="button"
          class="admin-shell__tabs-menu-item"
          :disabled="!canCloseLeftFor(activeTab)"
          @click="handleCloseLeftTarget(activeTab)"
        >
          <ArrowLeftToLine :size="13" class="admin-shell__menu-icon" aria-hidden="true" />
          <span>关闭左侧</span>
        </button>
        <button
          type="button"
          class="admin-shell__tabs-menu-item"
          :disabled="!canCloseRightFor(activeTab)"
          @click="handleCloseRightTarget(activeTab)"
        >
          <ArrowRightToLine :size="13" class="admin-shell__menu-icon" aria-hidden="true" />
          <span>关闭右侧</span>
        </button>
        <button
          type="button"
          class="admin-shell__tabs-menu-item"
          :disabled="!canCloseOtherFor(activeTab)"
          @click="handleCloseOtherTarget(activeTab)"
        >
          <MinusCircle :size="13" class="admin-shell__menu-icon" aria-hidden="true" />
          <span>关闭其他</span>
        </button>
        <div class="admin-shell__tabs-menu-divider" />
        <button
          type="button"
          class="admin-shell__tabs-menu-item is-danger"
          :disabled="!hasClosableTabs"
          @click="handleCloseAllTarget"
        >
          <Trash2 :size="13" class="admin-shell__menu-icon" aria-hidden="true" />
          <span>关闭全部</span>
        </button>
      </div>
    </div>

    <!-- Right-click Context Menu (Teleported to Body for accurate z-index and clipping avoidance) -->
    <Teleport to="body">
      <div
        v-if="contextMenuOpen && contextTargetTab"
        class="admin-shell__context-menu"
        :style="{ left: `${contextMenuX}px`, top: `${contextMenuY}px` }"
        role="menu"
        @click.stop
        @contextmenu.prevent
      >
        <button
          type="button"
          class="admin-shell__context-menu-item"
          role="menuitem"
          @click="handleRefreshTarget(contextTargetTab)"
        >
          <RotateCw :size="13" class="admin-shell__menu-icon" aria-hidden="true" />
          <span>重新加载</span>
        </button>
        <button
          type="button"
          class="admin-shell__context-menu-item"
          :disabled="!contextTargetTab?.closable"
          role="menuitem"
          @click="handleCloseTarget(contextTargetTab)"
        >
          <X :size="13" class="admin-shell__menu-icon" aria-hidden="true" />
          <span>关闭当前</span>
        </button>
        <button
          type="button"
          class="admin-shell__context-menu-item"
          :disabled="!canCloseLeftFor(contextTargetTab)"
          role="menuitem"
          @click="handleCloseLeftTarget(contextTargetTab)"
        >
          <ArrowLeftToLine :size="13" class="admin-shell__menu-icon" aria-hidden="true" />
          <span>关闭左侧</span>
        </button>
        <button
          type="button"
          class="admin-shell__context-menu-item"
          :disabled="!canCloseRightFor(contextTargetTab)"
          role="menuitem"
          @click="handleCloseRightTarget(contextTargetTab)"
        >
          <ArrowRightToLine :size="13" class="admin-shell__menu-icon" aria-hidden="true" />
          <span>关闭右侧</span>
        </button>
        <button
          type="button"
          class="admin-shell__context-menu-item"
          :disabled="!canCloseOtherFor(contextTargetTab)"
          role="menuitem"
          @click="handleCloseOtherTarget(contextTargetTab)"
        >
          <MinusCircle :size="13" class="admin-shell__menu-icon" aria-hidden="true" />
          <span>关闭其他</span>
        </button>
        <div class="admin-shell__tabs-menu-divider" />
        <button
          type="button"
          class="admin-shell__context-menu-item is-danger"
          :disabled="!hasClosableTabs"
          role="menuitem"
          @click="handleCloseAllTarget"
        >
          <Trash2 :size="13" class="admin-shell__menu-icon" aria-hidden="true" />
          <span>关闭全部</span>
        </button>
      </div>
    </Teleport>
  </nav>
</template>

<style scoped>
.admin-shell__tabs-container {
  position: relative;
  display: flex;
  align-items: center;
  height: 38px;
  min-height: 38px;
  max-height: 38px;
  background: var(--rr-color-surface);
  border-bottom: 1px solid var(--rr-color-border);
  box-sizing: border-box;
  padding: 0 8px 0 12px;
}

.admin-shell__tabs-scroll {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
}

.admin-shell__tabs-scroll::-webkit-scrollbar {
  display: none;
}

.admin-shell__tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  min-height: 28px !important;
  max-height: 28px !important;
  padding: 0 10px;
  box-sizing: border-box;
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-control);
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-muted);
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
  user-select: none;
  transition: all 130ms cubic-bezier(0.4, 0, 0.2, 1);
}

.admin-shell__tab:hover {
  color: var(--rr-color-text-strong);
  border-color: var(--rr-color-primary-border);
  background: var(--rr-color-surface);
}

.admin-shell__tab.active {
  background: var(--rr-color-primary-soft);
  border-color: var(--rr-color-primary-border);
  color: var(--rr-color-primary);
  font-weight: 600;
}

.admin-shell__tab-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--rr-color-primary);
  flex-shrink: 0;
}

.admin-shell__tab-label {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.admin-shell__tab-close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px !important;
  height: 14px !important;
  min-width: 14px !important;
  min-height: 14px !important;
  max-width: 14px !important;
  max-height: 14px !important;
  padding: 0 !important;
  margin: 0 0 0 2px !important;
  border: 0 !important;
  border-radius: 50%;
  background: transparent !important;
  color: inherit !important;
  cursor: pointer;
  line-height: 1;
  opacity: 0.55;
  transition: all 120ms ease;
}

/* Hover reveal for inactive tabs, always visible for active */
.admin-shell__tab:not(.active) .admin-shell__tab-close-btn {
  opacity: 0;
}
.admin-shell__tab:not(.active):hover .admin-shell__tab-close-btn {
  opacity: 0.65;
}
.admin-shell__tab.active .admin-shell__tab-close-btn {
  opacity: 0.75;
}
.admin-shell__tab-close-btn:hover {
  opacity: 1 !important;
  background: rgba(0, 0, 0, 0.08) !important;
}

/* Right-side Utility Tools */
.admin-shell__tabs-actions {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  margin-left: 6px;
  padding-left: 6px;
  border-left: 1px solid var(--rr-color-border);
}

.admin-shell__tabs-tool-btn {
  display: grid;
  place-items: center;
  width: 26px !important;
  height: 26px !important;
  min-width: 26px !important;
  min-height: 26px !important;
  max-width: 26px !important;
  max-height: 26px !important;
  padding: 0 !important;
  margin: 0 !important;
  border: 1px solid transparent !important;
  border-radius: var(--rr-radius-control);
  background: transparent !important;
  color: var(--rr-color-text-muted) !important;
  cursor: pointer;
  transition: all 120ms ease;
}

.admin-shell__tabs-tool-btn:hover,
.admin-shell__tabs-tool-btn.is-open {
  background: var(--rr-color-surface-subtle) !important;
  color: var(--rr-color-text-strong) !important;
  border-color: var(--rr-color-border) !important;
}

.admin-shell__tabs-menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 1000;
  min-width: 136px;
  padding: 4px;
  background: var(--rr-color-surface);
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-panel);
  box-shadow: var(--rr-shadow-overlay, 0 6px 16px rgba(0, 0, 0, 0.12));
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.admin-shell__tabs-menu-item {
  width: 100%;
  min-height: 30px !important;
  max-height: 30px !important;
  padding: 0 10px !important;
  margin: 0 !important;
  border: 0 !important;
  border-radius: var(--rr-radius-control);
  background: transparent !important;
  color: var(--rr-color-text) !important;
  font-size: 12px;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 120ms ease;
}

.admin-shell__tabs-menu-item:hover:not(:disabled) {
  background: var(--rr-color-surface-subtle) !important;
  color: var(--rr-color-primary) !important;
}

.admin-shell__tabs-menu-item:disabled {
  color: var(--rr-color-text-subtle) !important;
  cursor: not-allowed;
  opacity: 0.45;
}

.admin-shell__tabs-menu-item.is-danger:hover:not(:disabled) {
  color: var(--rr-color-danger) !important;
  background: var(--rr-color-danger-soft) !important;
}

.admin-shell__tabs-menu-divider {
  height: 1px;
  margin: 3px 0;
  background: var(--rr-color-border);
}

.admin-shell__menu-icon {
  flex-shrink: 0;
  color: inherit;
  opacity: 0.75;
}
</style>

<!-- Global Context Menu Styling -->
<style>
.admin-shell__context-menu {
  position: fixed;
  z-index: 9999;
  min-width: 136px;
  padding: 4px;
  background: var(--rr-color-surface);
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-panel);
  box-shadow: var(--rr-shadow-overlay, 0 8px 24px rgba(0, 0, 0, 0.16));
  display: flex;
  flex-direction: column;
  gap: 2px;
  animation: rr-context-menu-fade 120ms ease-out;
}

@keyframes rr-context-menu-fade {
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.admin-shell__context-menu-item {
  width: 100%;
  min-height: 30px !important;
  max-height: 30px !important;
  padding: 0 10px !important;
  margin: 0 !important;
  border: 0 !important;
  border-radius: var(--rr-radius-control);
  background: transparent !important;
  color: var(--rr-color-text) !important;
  font-size: 12px;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 120ms ease;
  user-select: none;
}

.admin-shell__context-menu-item:hover:not(:disabled) {
  background: var(--rr-color-surface-subtle) !important;
  color: var(--rr-color-primary) !important;
}

.admin-shell__context-menu-item:disabled {
  color: var(--rr-color-text-subtle) !important;
  cursor: not-allowed;
  opacity: 0.45;
}

.admin-shell__context-menu-item.is-danger:hover:not(:disabled) {
  color: var(--rr-color-danger) !important;
  background: var(--rr-color-danger-soft) !important;
}
</style>
